/**
 * Cloudflare Turnstile integration for the signup wizard.
 *
 * The widget on the final step uses `data-execution="execute"`, which
 * makes it render a placeholder at page load but skip the challenge
 * until `turnstile.execute()` is called. The flow:
 *
 *   1. User reaches step 8 and clicks Submit.
 *   2. `getTurnstileToken()` calls `turnstile.reset(widget)` then
 *      `turnstile.execute(widget)`. The reset clears any previously
 *      issued single-use token from an earlier attempt; the execute
 *      runs a fresh challenge.
 *   3. Cloudflare runs the challenge (invisible for most traffic,
 *      interactive for suspicious).
 *   4. The widget invokes `window.onSignupTurnstileToken(token)`, which
 *      resolves the pending Promise.
 *   5. apiSubmission.js receives the fresh token and POSTs it.
 *
 * A timeout resolves with the hidden-input value (typically empty when
 * a challenge fails to complete) so the request still goes out. The
 * backend treats absent / empty as `STATE_MISSING` and logs without
 * blocking while Turnstile remains in Phase 1b.
 */

// Caps the wait for `turnstile.execute()` to call back. A typical
// invisible challenge resolves in 100-300 ms. Interactive challenges
// (Cloudflare's checkbox, shown to suspicious traffic) need enough
// headroom for a real user to notice and click. 30 s balances giving
// interactive users time against bounding worst-case submit latency
// when the widget itself has died.
const TIMEOUT_MS = 30000;

let pendingResolvers = [];
let pendingTimeoutId = null;

function readHiddenInput() {
    return document.querySelector('#signupForm input[name="cf-turnstile-response"]')?.value || '';
}

function clearPending() {
    if (pendingTimeoutId !== null) {
        clearTimeout(pendingTimeoutId);
        pendingTimeoutId = null;
    }
    const resolvers = pendingResolvers;
    pendingResolvers = [];
    return resolvers;
}

function resolvePendingWith(value) {
    const resolvers = clearPending();
    for (const resolve of resolvers) {
        resolve(value);
    }
}

window.onSignupTurnstileToken = function (token) {
    resolvePendingWith(token || '');
};

window.onSignupTurnstileError = function (errorCode) {
    console.warn('[Signup] Turnstile error:', errorCode);
    // Fall back to the hidden input value. The backend logs `failed` /
    // `missing` and continues to allow signup while Turnstile is in
    // Phase 1b.
    resolvePendingWith(readHiddenInput());
};

/**
 * Returns a fresh Turnstile token for the current submission.
 *
 * Re-entrant: the upstream `isSubmitting` guard in submitSignup
 * normally prevents two concurrent calls. If one does slip through
 * (an Enter keypress racing the form submit handler), the second
 * caller joins the in-flight resolver list instead of starting a
 * second challenge, and the deadline is reset so the late caller
 * gets a full TIMEOUT_MS wait.
 */
export function getTurnstileToken() {
    if (typeof window.turnstile === 'undefined') {
        return Promise.resolve(readHiddenInput());
    }

    const widget = document.querySelector('#signupForm .cf-turnstile');
    if (!widget) {
        return Promise.resolve(readHiddenInput());
    }

    const promise = new Promise((resolve) => {
        pendingResolvers.push(resolve);
    });

    if (pendingTimeoutId !== null) {
        clearTimeout(pendingTimeoutId);
    }
    pendingTimeoutId = setTimeout(() => {
        resolvePendingWith(readHiddenInput());
    }, TIMEOUT_MS);

    if (pendingResolvers.length > 1) {
        return promise;
    }

    try {
        // Clear any prior single-use token from a previous attempt.
        // A submit that hit a server validation error leaves the
        // widget holding a consumed token; without reset, execute()
        // can return the same token and the next POST fails with
        // `timeout-or-duplicate`.
        window.turnstile.reset(widget);
        window.turnstile.execute(widget);
    } catch (e) {
        console.warn('[Signup] Turnstile execute failed:', e);
        resolvePendingWith(readHiddenInput());
    }

    return promise;
}
